import type {
  ClinicianWorkbenchApprovalSummary,
  ClinicianWorkbenchConversation,
  ClinicianWorkbenchConversationMessage,
  ClinicianWorkbenchClinicalNote,
  ClinicianWorkbenchDeliverySummary,
  ClinicianWorkbenchDerivedFlagKey,
  ClinicianWorkbenchDetailSection,
  ClinicianWorkbenchDocumentSummary,
  ClinicianWorkbenchTaskAssignmentSummary,
  ClinicianWorkbenchVoiceCallSummary,
} from "./clinician-workbench-contract.js";
import type { WorkspaceChangedScopeV1 } from "./workspace-refresh-contract.js";

export const patientWorkspaceSummaryContractVersion =
  "patient-workspace-summary-v1" as const;

export const patientWorkspaceSummaryRoutePath =
  "/api/v1/patient-workspace/summary" as const;

export const patientPortalFeatureDefinitionsV1 = [
  {
    flagKey: "patient.feature.appointments",
    moduleId: "appointments",
    label: "Appointments",
    defaultEnabled: true,
  },
  {
    flagKey: "patient.feature.procedures",
    moduleId: "procedures",
    label: "Procedures",
    defaultEnabled: true,
  },
  {
    flagKey: "patient.feature.medications",
    moduleId: "medications",
    label: "Medications",
    defaultEnabled: true,
  },
  {
    flagKey: "patient.feature.documents",
    moduleId: "documents",
    label: "Documents",
    defaultEnabled: true,
  },
  {
    flagKey: "patient.feature.messaging",
    moduleId: "messages",
    label: "Messaging",
    defaultEnabled: true,
  },
  {
    flagKey: "patient.feature.calls",
    moduleId: "calls",
    label: "Calls",
    defaultEnabled: true,
  },
] as const;

export type PatientPortalFeatureDefinitionV1 =
  (typeof patientPortalFeatureDefinitionsV1)[number];

export type PatientPortalFeatureFlagKeyV1 =
  PatientPortalFeatureDefinitionV1["flagKey"];

export type PatientPortalFeatureModuleIdV1 =
  PatientPortalFeatureDefinitionV1["moduleId"];

export type PatientPortalFeatureFlagsV1 = Record<
  PatientPortalFeatureFlagKeyV1,
  boolean
>;

export const patientPortalDefaultFeatureFlagsV1 =
  patientPortalFeatureDefinitionsV1.reduce((flags, definition) => {
    flags[definition.flagKey] = definition.defaultEnabled;
    return flags;
  }, {} as PatientPortalFeatureFlagsV1);

export const patientWorkspaceDocumentCommandContractVersion =
  "patient-workspace-document-command-v1" as const;

export const patientWorkspaceDocumentCommandRoutePath =
  "/api/v1/patient-workspace/documents" as const;

export const patientWorkspaceDocumentDownloadContractVersion =
  "patient-workspace-document-download-v1" as const;

export const patientWorkspaceDocumentDownloadRoutePath =
  "/api/v1/patient-workspace/documents/download" as const;

export const patientWorkspaceDocumentUploadIntentContractVersion =
  "patient-workspace-document-upload-intent-v1" as const;

export const patientWorkspaceDocumentUploadIntentRoutePath =
  "/api/v1/patient-workspace/documents/upload-intent" as const;

export const patientWorkspaceDocumentCompleteUploadContractVersion =
  "patient-workspace-document-complete-upload-v1" as const;

export const patientWorkspaceDocumentCompleteUploadRoutePath =
  "/api/v1/patient-workspace/documents/complete-upload" as const;

export const patientWorkspaceDocumentStatusContractVersion =
  "patient-workspace-document-status-v1" as const;

export const patientWorkspaceDocumentStatusRoutePathPattern =
  "/api/v1/patient-workspace/documents/{documentId}/status" as const;

export const patientWorkspaceMessageCommandContractVersion =
  "patient-workspace-message-command-v1" as const;

export const patientWorkspaceMessageCommandRoutePath =
  "/api/v1/patient-workspace/messages" as const;

export const patientWorkspaceOnboardingCommandContractVersion =
  "patient-workspace-onboarding-command-v1" as const;

export const patientWorkspaceOnboardingCommandRoutePath =
  "/api/v1/patient-workspace/onboarding" as const;

export const patientWorkspaceSpeechContractVersion =
  "patient-workspace-speech-v1" as const;

export const patientWorkspaceSpeechRoutePath =
  "/api/v1/patient-workspace/speech" as const;

export type PatientWorkspaceSpeechVoiceIdV1 =
  | "Danielle"
  | "Joanna"
  | "Ruth"
  | "Salli"
  | "Tiffany";

export type PatientWorkspaceSpeechEngineV1 = "generative" | "neural";

export type PatientWorkspaceEntryPointRouteV1 =
  | "overview"
  | "onboarding"
  | "appointments"
  | "procedures"
  | "medications"
  | "documents"
  | "calls"
  | "messages";

export interface PatientWorkspaceOnboardingStateV1 {
  initialFlowComplete: boolean;
  completedAt: string | null;
  entryPointRoute: PatientWorkspaceEntryPointRouteV1;
  auth: {
    status: "not-started" | "challenge-issued" | "verified" | "locked";
    method: "portal-session" | "portal-otp" | "test-otp" | null;
    verifiedAt: string | null;
    phoneNumberLast4: string | null;
    challengeIssuedAt: string | null;
    challengeExpiresAt: string | null;
    attemptsRemaining: number | null;
    deliveryMode: "portal" | "sms-gated" | null;
    smsDispatchEnabled: boolean;
  };
  preferences: {
    remindersEnabled: boolean;
    reminderChannel: "portal";
    wakeUpTime: string | null;
    updatedAt: string | null;
  };
}

export interface PatientWorkspaceSummaryPatientV1 {
  patientId: string;
  displayName: string;
  identifier: string;
  legalName: string | null;
  dateOfBirth: string | null;
  medicalRecordNumber: string | null;
  summaryText: string;
  statusLabel: string;
  derivedFlagKeys: ClinicianWorkbenchDerivedFlagKey[];
  coordinatorLabel: string;
  programLabel: string;
  nextAppointmentText: string;
  materialChangeToken: string;
  contactWindowText: string;
  procedureSummaryText: string;
  medicationSummaryText: string;
  unreadMessageCount: number;
  pendingDocumentCount: number;
  requiresAttention: boolean;
  conversation: ClinicianWorkbenchConversation;
  taskAssignments: ClinicianWorkbenchTaskAssignmentSummary[];
  approvalSummaries: ClinicianWorkbenchApprovalSummary[];
  deliverySummaries: ClinicianWorkbenchDeliverySummary[];
  documentSummaries: ClinicianWorkbenchDocumentSummary[];
  voiceCallSummaries: ClinicianWorkbenchVoiceCallSummary[];
  detailSections: ClinicianWorkbenchDetailSection[];
  clinicalNotes: ClinicianWorkbenchClinicalNote[];
  onboarding: PatientWorkspaceOnboardingStateV1;
}

export interface PatientWorkspaceSummaryResponseV1 {
  schemaVersion: typeof patientWorkspaceSummaryContractVersion;
  tenantId: string;
  patientId: string;
  projectionVersion: string;
  generatedAt: string;
  featureFlags: PatientPortalFeatureFlagsV1;
  patient: PatientWorkspaceSummaryPatientV1;
}

export interface PatientWorkspaceDocumentCommandRequestV1 {
  schemaVersion: typeof patientWorkspaceDocumentCommandContractVersion;
  tenantId: string;
  siteId?: string | null;
  patientId?: string | null;
  label: string;
  summaryText?: string | null;
  createdByLabel?: string | null;
}

export interface PatientWorkspaceDocumentCommandResponseV1 {
  schemaVersion: typeof patientWorkspaceDocumentCommandContractVersion;
  tenantId: string;
  patientId: string;
  document: ClinicianWorkbenchDocumentSummary;
  summary: PatientWorkspaceSummaryResponseV1;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export interface PatientWorkspaceDocumentUploadIntentRequestV1 {
  schemaVersion: typeof patientWorkspaceDocumentUploadIntentContractVersion;
  tenantId: string;
  siteId?: string | null;
  patientId?: string | null;
  filename: string;
  mediaType: string;
  byteLength: number;
  sha256?: string | null;
  title?: string | null;
  documentTypeKey?: string | null;
}

export interface PatientWorkspaceDocumentUploadIntentResponseV1 {
  schemaVersion: typeof patientWorkspaceDocumentUploadIntentContractVersion;
  tenantId: string;
  patientId: string;
  uploadId: string;
  documentId: string;
  uploadMethod: "PUT";
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  objectKey: string;
  expiresAt: string;
  maxByteLength: number;
}

export interface PatientWorkspaceDocumentCompleteUploadRequestV1 {
  schemaVersion: typeof patientWorkspaceDocumentCompleteUploadContractVersion;
  tenantId: string;
  siteId?: string | null;
  patientId?: string | null;
  uploadId: string;
  documentId: string;
  byteLength: number;
  sha256?: string | null;
  title?: string | null;
  documentTypeKey?: string | null;
}

export interface PatientWorkspaceDocumentIngestionStatusV1 {
  documentId: string;
  uploadId: string | null;
  ingestionStatus: "received" | "extracting" | "ready" | "failed" | "quarantined";
  extractionStatus: "not_needed" | "pending" | "running" | "ready" | "failed";
  extractionMethod: string | null;
  summaryText: string | null;
  failureReason: string | null;
  aiContextEligible: boolean;
  reviewStatus: "not_required" | "pending_review" | "reviewed" | "rejected";
  previewStatus: "pending" | "ready" | "failed";
  downloadReady: boolean;
  originalSha256: string | null;
  extractedAt: string | null;
  updatedAt: string;
}

export interface PatientWorkspaceDocumentCompleteUploadResponseV1 {
  schemaVersion: typeof patientWorkspaceDocumentCompleteUploadContractVersion;
  tenantId: string;
  patientId: string;
  document: ClinicianWorkbenchDocumentSummary;
  ingestion: PatientWorkspaceDocumentIngestionStatusV1;
  summary: PatientWorkspaceSummaryResponseV1;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export interface PatientWorkspaceDocumentStatusResponseV1 {
  schemaVersion: typeof patientWorkspaceDocumentStatusContractVersion;
  tenantId: string;
  patientId: string;
  ingestion: PatientWorkspaceDocumentIngestionStatusV1;
}

export interface PatientWorkspaceMessageCommandRequestV1 {
  schemaVersion: typeof patientWorkspaceMessageCommandContractVersion;
  tenantId: string;
  siteId?: string | null;
  patientId?: string | null;
  bodyText: string;
}

export interface PatientWorkspaceMessageCommandResponseV1 {
  schemaVersion: typeof patientWorkspaceMessageCommandContractVersion;
  tenantId: string;
  patientId: string;
  patientMessage: ClinicianWorkbenchConversationMessage;
  agentDraftMessage: ClinicianWorkbenchConversationMessage;
  summary: PatientWorkspaceSummaryResponseV1;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export interface PatientWorkspaceOnboardingCommandRequestV1 {
  schemaVersion: typeof patientWorkspaceOnboardingCommandContractVersion;
  tenantId: string;
  siteId?: string | null;
  patientId?: string | null;
  initialFlowComplete?: boolean | null;
  entryPointRoute?: PatientWorkspaceEntryPointRouteV1 | null;
  wakeUpTime?: string | null;
  remindersEnabled?: boolean | null;
  authAttestation?: {
    method: "portal-session" | "test-otp";
    phoneNumber?: string | null;
    otpCode?: string | null;
  } | null;
  authChallenge?: {
    action: "request" | "verify";
    phoneNumber?: string | null;
    otpCode?: string | null;
  } | null;
}

export interface PatientWorkspaceOnboardingCommandResponseV1 {
  schemaVersion: typeof patientWorkspaceOnboardingCommandContractVersion;
  tenantId: string;
  patientId: string;
  onboarding: PatientWorkspaceOnboardingStateV1;
  authChallenge:
    | {
        action: "request" | "verify";
        status: "challenge-issued" | "verified" | "failed" | "expired" | "locked";
        phoneNumberLast4: string | null;
        expiresAt: string | null;
        attemptsRemaining: number | null;
        smsDispatchEnabled: boolean;
        deliveryMode: "portal" | "sms-gated" | null;
        developmentCode?: string | null;
      }
    | null;
  summary: PatientWorkspaceSummaryResponseV1 | null;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export interface PatientWorkspaceSpeechRequestV1 {
  schemaVersion: typeof patientWorkspaceSpeechContractVersion;
  tenantId: string;
  patientId?: string | null;
  bodyText: string;
  voiceId?: PatientWorkspaceSpeechVoiceIdV1 | null;
}
